import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import { Alert, Box, Typography } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { INK } from "../../../theme/tokens";
import { HoverActionCell } from "../../Settings/DepartmentConfiguration/forms/hrisMapping/utils";

/** A working copy of one pbj_job row. `key` is the stable grid id (server id or a client tempId). */
export interface JobDraft {
	key: string;
	id?: string;
	tempId?: string;
	code: string;
	description: string;
}

interface ManageJobsDialogProps {
	open: boolean;
	/** Discards the edits made since the dialog opened. */
	onCancel: () => void;
	/** Hands the buffered changeset to the tab's save confirmation. */
	onSave: () => void;
	/** Whether anything on the tab (jobs or mappings) differs from what's saved. */
	hasChanges: boolean;
	jobs: JobDraft[];
	onChange: (next: JobDraft[]) => void;
	/** role_strings currently mapped to a given job code — used to block deletes. */
	rolesReferencingCode: (code: string) => string[];
}

/** CMS publishes 40 PBJ job categories — mirrors the backend's save validation. */
const MIN_PBJ_CODE = 1;
const MAX_PBJ_CODE = 40;
const DIGITS_ONLY = /^\d+$/;

let tempIdCounter = 0;
const nextTempId = (): string => `tmp-${++tempIdCounter}`;

const isValidCode = (code: string): boolean => DIGITS_ONLY.test(code) && Number(code) >= MIN_PBJ_CODE && Number(code) <= MAX_PBJ_CODE;

/** The first reason the draft can't be saved, as a translation key + params — the same rules the backend enforces. */
const findDraftProblem = (jobs: JobDraft[]): { key: string; params?: Record<string, string> } | null => {
	if (jobs.some((j) => !isValidCode(j.code.trim())))
		return { key: "pbj_model.jobs.manage.invalid_code", params: { min: `${MIN_PBJ_CODE}`, max: `${MAX_PBJ_CODE}` } };
	if (jobs.some((j) => j.description.trim() === "")) return { key: "pbj_model.jobs.manage.invalid_description" };
	const seen = new Set<string>();
	for (const job of jobs) {
		const code = job.code.trim();
		if (seen.has(code)) return { key: "pbj_model.jobs.manage.duplicate_code", params: { code } };
		seen.add(code);
	}
	return null;
};

/** Empty editable cells show a muted hint, so a freshly added row reads as "fill me in" rather than blank. */
const renderWithPlaceholder = (placeholder: string) => (params: GridRenderCellParams<JobDraft, string>) =>
	params.value ? params.value : <Typography sx={{ color: INK.MUTED, fontSize: "inherit", fontStyle: "italic" }}>{placeholder}</Typography>;

/**
 * CRUD editor for the department's PBJ job registry (code + description). Edits buffer into the
 * parent's draft; Save hands them to the tab's apply confirmation and Cancel drops them. Deleting a
 * job that role_strings still map to is blocked here (the parent knows the effective mapping).
 */
export const ManageJobsDialog: FC<ManageJobsDialogProps> = ({ open, onCancel, onSave, hasChanges, jobs, onChange, rolesReferencingCode }) => {
	const { t } = useTranslation();
	const [blocked, setBlocked] = useState<{ code: string; roleStrings: string[] } | null>(null);
	const problem = useMemo(() => findDraftProblem(jobs), [jobs]);

	const handleAdd = useCallback(() => {
		const tempId = nextTempId();
		onChange([{ key: tempId, tempId, code: "", description: "" }, ...jobs]);
	}, [jobs, onChange]);

	const handleDelete = useCallback(
		(job: JobDraft) => {
			const refs = rolesReferencingCode(job.code);
			if (refs.length > 0) {
				setBlocked({ code: job.code, roleStrings: refs });
				return;
			}
			setBlocked(null);
			onChange(jobs.filter((j) => j.key !== job.key));
		},
		[jobs, onChange, rolesReferencingCode],
	);

	const handleCancel = useCallback(() => {
		setBlocked(null);
		onCancel();
	}, [onCancel]);

	const processRowUpdate = useCallback(
		(newRow: JobDraft): JobDraft => {
			onChange(jobs.map((j) => (j.key === newRow.key ? { ...j, code: newRow.code.trim(), description: newRow.description } : j)));
			return newRow;
		},
		[jobs, onChange],
	);

	const columns: GridColDef<JobDraft>[] = useMemo(
		() => [
			{
				field: "code",
				headerName: t("pbj_model.jobs.column.code"),
				width: 120,
				editable: true,
				renderCell: renderWithPlaceholder(t("pbj_model.jobs.manage.code_placeholder", { min: MIN_PBJ_CODE, max: MAX_PBJ_CODE })),
			},
			{
				field: "description",
				headerName: t("pbj_model.jobs.column.description"),
				flex: 1,
				minWidth: 260,
				editable: true,
				renderCell: renderWithPlaceholder(t("pbj_model.jobs.manage.description_placeholder")),
			},
			{
				field: "actions",
				headerName: "",
				width: 64,
				sortable: false,
				align: "center",
				display: "flex",
				renderCell: (params) => (
					<HoverActionCell
						show
						actionIcon={<DeleteOutlineIcon fontSize="small" color="error" />}
						tooltip={t("pbj_model.jobs.delete")}
						onAction={() => handleDelete(params.row)}
					/>
				),
			},
		],
		[t, handleDelete],
	);

	const body = (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minHeight: 360 }}>
			<Typography variant="body2" sx={{ color: INK.SECONDARY }} data-testid="pbj-jobs-manage-hint">
				{t("pbj_model.jobs.manage.hint", { min: MIN_PBJ_CODE, max: MAX_PBJ_CODE })}
			</Typography>
			{blocked && (
				<Alert severity="warning" onClose={() => setBlocked(null)} data-testid="pbj-jobs-delete-blocked">
					{t("pbj_model.jobs.delete_blocked", { code: blocked.code, roleStrings: blocked.roleStrings.join(", ") })}
				</Alert>
			)}
			<SystemDataGrid<JobDraft>
				rows={jobs}
				columns={columns}
				getRowId={(row) => row.key}
				searchableColumns={["code", "description"]}
				searchPlaceholder={t("pbj_model.jobs.search.jobs")}
				processRowUpdate={processRowUpdate}
				leadingToolbarNodes={
					<SystemButton
						buttonVariant="secondary"
						onClick={handleAdd}
						leadingIcon={<AddIcon sx={{ fontSize: 18 }} />}
						noFullWidthMobile
						data-testid="pbj-jobs-add"
					>
						{t("pbj_model.jobs.add")}
					</SystemButton>
				}
				dataTestId="pbj-jobs-registry-grid"
			/>
			{problem && (
				<Alert severity="info" data-testid="pbj-jobs-manage-problem">
					{t(problem.key, problem.params)}
				</Alert>
			)}
		</Box>
	);

	return (
		<SystemDialog
			open={open}
			onClose={handleCancel}
			title={t("pbj_model.jobs.manage_jobs")}
			body={body}
			confirm={t("pbj_model.jobs.save")}
			confirmButtonVariant="primary"
			confirmDisabled={!hasChanges || problem !== null}
			confirmTooltip={hasChanges ? undefined : t("pbj_model.jobs.manage.no_changes")}
			onConfirm={onSave}
			maxWidth="sm"
			fullWidth
			showCloseIcon
			dataTestId="pbj-jobs-manage-dialog"
		/>
	);
};
